import { useState, useEffect, useCallback } from 'react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { X, Download, Loader2, Package, FileArchive } from 'lucide-react';
import { api } from '@/lib/api-client';
import { toast } from 'sonner';

interface ExportCapability {
    name: string;
    type: string;
    file_path: string;
}

interface ExportApplicationDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    appKey: string;
    onSuccess?: () => void;
}

export default function ExportApplicationDrawer({
    isOpen,
    onClose,
    appKey,
    onSuccess,
}: ExportApplicationDrawerProps) {
    const [capabilities, setCapabilities] = useState<ExportCapability[]>([]);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    const fetchPreview = useCallback(async () => {
        setLoadingPreview(true);
        setPreviewError(null);
        try {
            const res = await api.get(`/applications/${appKey}/export/preview`);
            setCapabilities(res.data.capabilities ?? []);
        } catch {
            setPreviewError('Failed to load export preview');
            toast.error('Failed to load export preview');
        } finally {
            setLoadingPreview(false);
        }
    }, [appKey]);

    useEffect(() => {
        if (isOpen) {
            setCapabilities([]);
            setPreviewError(null);
            fetchPreview();
        }
    }, [isOpen, fetchPreview]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const res = await api.get(`/applications/${appKey}/export`, {
                responseType: 'blob',
            });

            const blob = new Blob([res.data], { type: 'application/zip' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${appKey}-export.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast.success('Download started');
            onSuccess?.();
            onClose();
        } catch {
            toast.error('Failed to download export');
        } finally {
            setDownloading(false);
        }
    };

    const canDownload = !loadingPreview && !previewError && capabilities.length > 0;

    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                        {/* Header */}
                        <div className="px-8 py-4 border-b border-slate-100 bg-slate-50/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-100">
                                        <FileArchive className="h-5 w-5 text-cyan-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-slate-900">
                                            Export Application
                                        </h2>
                                        <p className="text-sm text-slate-500">
                                            {appKey} &mdash; working revision
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onClose();
                                    }}
                                    className="rounded-full p-1 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-slate-900"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto px-8 py-6">
                            {loadingPreview ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                                </div>
                            ) : previewError ? (
                                <div className="flex flex-col items-center justify-center py-12 gap-3">
                                    <p className="text-sm text-red-600">{previewError}</p>
                                    <button
                                        onClick={fetchPreview}
                                        className="text-sm text-cyan-600 hover:underline"
                                    >
                                        Retry
                                    </button>
                                </div>
                            ) : capabilities.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 gap-2">
                                    <Package size={32} className="text-slate-300" />
                                    <p className="text-sm text-slate-500">
                                        No capabilities to export
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="rounded-lg border border-slate-200 overflow-hidden">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="bg-slate-50 border-b border-slate-200">
                                                    <th className="text-left px-4 py-2 font-medium text-slate-600">
                                                        Name
                                                    </th>
                                                    <th className="text-left px-4 py-2 font-medium text-slate-600">
                                                        Path in ZIP
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {capabilities.map((cap, i) => (
                                                    <tr
                                                        key={cap.file_path}
                                                        className={
                                                            i % 2 === 0
                                                                ? 'bg-white'
                                                                : 'bg-slate-50/50'
                                                        }
                                                    >
                                                        <td className="px-4 py-2 text-slate-900 font-medium">
                                                            {cap.name}
                                                        </td>
                                                        <td className="px-4 py-2 text-slate-500 font-mono text-xs">
                                                            {cap.file_path}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <p className="text-xs text-slate-400 text-right">
                                        Total: {capabilities.length} capability
                                        {capabilities.length !== 1 ? ' files' : ' file'}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-8 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end items-center gap-3">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClose();
                                }}
                                disabled={downloading}
                                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:shadow disabled:opacity-50"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDownload();
                                }}
                                disabled={!canDownload || downloading}
                                className="flex items-center gap-2 rounded-lg bg-btn-primary px-4 py-2 text-sm font-medium text-white shadow-md transition-all hover:bg-btn-primary-hover hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {downloading ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Downloading...
                                    </>
                                ) : (
                                    <>
                                        <Download size={16} />
                                        Download zip
                                    </>
                                )}
                            </button>
                        </div>
        </SlideDrawer>
    );
}
